/**
 * The message catalogs, aggregated from the per-feature files.
 *
 * ## Why per-feature files, merged here
 *
 * Splitting the catalog by feature (`en/notebook.ts`, `en/calendar.ts`, …) lets the string sweep
 * proceed one feature at a time without every edit landing in the same 2000-line file. This index
 * is the ONE place that stitches them together; it rarely changes (only when a whole new feature
 * area appears), so adding a key never means touching a shared file.
 *
 * ## The coverage guarantee
 *
 * `en` is canonical: `MessageKey = keyof typeof en`. Each German feature file is typed
 * `Record<keyof typeof enFeature, string>`, so a missing or misspelled German key is a compile
 * error in that file. `de` here is additionally pinned to `Record<MessageKey, string>` as a
 * belt-and-braces check across the merged whole. There is no runtime "missing translation" state
 * that can ship — `tsc` (which CI runs, `111-frontend-architecture.md` §1) rejects it.
 */
import { ai } from './en/ai';
import { auth } from './en/auth';
import { calendar } from './en/calendar';
import { common } from './en/common';
import { editor } from './en/editor';
import { notebook } from './en/notebook';
import { offline } from './en/offline';
import { search } from './en/search';
import { settings } from './en/settings';
import { shell } from './en/shell';
import { tags } from './en/tags';

import { ai as aiDe } from './de/ai';
import { auth as authDe } from './de/auth';
import { calendar as calendarDe } from './de/calendar';
import { common as commonDe } from './de/common';
import { editor as editorDe } from './de/editor';
import { notebook as notebookDe } from './de/notebook';
import { offline as offlineDe } from './de/offline';
import { search as searchDe } from './de/search';
import { settings as settingsDe } from './de/settings';
import { shell as shellDe } from './de/shell';
import { tags as tagsDe } from './de/tags';

export const en = {
  ...common,
  ...shell,
  ...notebook,
  ...editor,
  ...calendar,
  ...ai,
  ...search,
  ...settings,
  ...tags,
  ...auth,
  ...offline,
} as const;

export type MessageKey = keyof typeof en;

const de: Record<MessageKey, string> = {
  ...commonDe,
  ...shellDe,
  ...notebookDe,
  ...editorDe,
  ...calendarDe,
  ...aiDe,
  ...searchDe,
  ...settingsDe,
  ...tagsDe,
  ...authDe,
  ...offlineDe,
};

export const messages: Record<'en' | 'de', Record<MessageKey, string>> = { en, de };
